import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const disciplineKeys = {
  all: ["disciplines"] as const,
  list: (opts?: { includeInactive?: boolean }) => ["disciplines", "list", opts ?? {}] as const,
} as const;

// Per-level help text for a Discipline's fixed 1–5 rating scale (distinct from
// disciplineRatingKeys, which caches per-Resource rating values).
export const disciplineRatingLevelKeys = {
  all: ["discipline-rating-levels"] as const,
  list: (disciplineId: string) => ["discipline-rating-levels", "list", disciplineId] as const,
} as const;

export const podShapeKeys = {
  all: ["pod-shapes"] as const,
  list: (opts?: { includeInactive?: boolean }) => ["pod-shapes", "list", opts ?? {}] as const,
  tiers: () => ["pod-shapes", "tiers"] as const,
} as const;

export const resourceDisciplineKeys = {
  all: ["resource-disciplines"] as const,
  list: (resourceId: string) => ["resource-disciplines", "list", resourceId] as const,
} as const;

export const disciplineRatingKeys = {
  all: ["discipline-ratings"] as const,
  list: (resourceId: string) => ["discipline-ratings", "list", resourceId] as const,
} as const;

export const engagementStandardTierKeys = {
  all: ["engagement-standard-tiers"] as const,
  expectationPenaltyStep: (tierId: string) =>
    ["engagement-standard-tiers", "expectation-penalty-step", tierId] as const,
} as const;

export const solutionPodPositionKeys = {
  all: ["solution-pod-positions"] as const,
  list: (solutionId: string) => ["solution-pod-positions", "list", solutionId] as const,
} as const;

export const adminConstraintKeys = {
  all: ["admin", "constraints"] as const,
  list: () => ["admin", "constraints", "list"] as const,
  groups: () => ["admin", "constraints", "groups"] as const,
} as const;

export const adminIntensityLevelKeys = {
  all: ["admin", "intensity-levels"] as const,
  list: () => ["admin", "intensity-levels", "list"] as const,
} as const;

export const staffingRequestKeys = {
  all: ["staffing-requests"] as const,
  list: (params?: {
    statuses?: string[];
    linked?: string;
    cursor?: string;
    direction?: string;
    limit?: number;
  }) => ["staffing-requests", "list", params ?? {}] as const,
  detail: (id: string) => ["staffing-requests", "detail", id] as const,
} as const;

export const integrationSolutionKeys = {
  all: ["integration-solutions"] as const,
  list: () => ["integration-solutions", "list"] as const,
} as const;

export const integrationAccountKeys = {
  all: ["integration-accounts"] as const,
  list: () => ["integration-accounts", "list"] as const,
} as const;

export const integrationDemandLinkKeys = {
  all: ["integration-demand-links"] as const,
  list: () => ["integration-demand-links", "list"] as const,
} as const;

export const integrationResourceKeys = {
  all: ["integration-resources"] as const,
  list: () => ["integration-resources", "list"] as const,
} as const;

export const integrationTimeEntryKeys = {
  all: ["integration-time-entries"] as const,
  list: () => ["integration-time-entries", "list"] as const,
} as const;

export const guidanceKeys = {
  all: ["guidance"] as const,
  list: () => ["guidance", "list"] as const,
  /** One Resource's Solutions × months guidance/actuals matrix (drawer drill-down). */
  matrix: (resourceId: string) => ["guidance", "matrix", resourceId] as const,
} as const;

export const accountKeys = {
  all: ["accounts"] as const,
  list: (params?: { cursor?: string; direction?: string; search?: string; limit?: number }) =>
    ["accounts", "list", params ?? {}] as const,
  detail: (id: string) => ["accounts", "detail", id] as const,
  tierImpact: (id: string, tierId?: string) =>
    ["accounts", "tier-impact", id, tierId ?? "current"] as const,
  constraints: (id: string) => ["accounts", "constraints", id] as const,
  constraintLibrary: () => ["accounts", "constraint-library"] as const,
} as const;

export const resourceEngagementsKeys = {
  all: ["resource-engagements"] as const,
  detail: (resourceId: string) => ["resource-engagements", "detail", resourceId] as const,
} as const;

export const solutionWorkloadKeys = {
  all: ["solution-workload"] as const,
  detail: (solutionId: string) => ["solution-workload", "detail", solutionId] as const,
} as const;

export const solutionDemandLinkKeys = {
  all: ["solution-demand-links"] as const,
  detail: (solutionId: string) => ["solution-demand-links", "detail", solutionId] as const,
} as const;

export const solutionPipelineKeys = {
  all: ["solution-pipeline"] as const,
  detail: (solutionId: string) => ["solution-pipeline", "detail", solutionId] as const,
} as const;

export const systemConfigKeys = {
  all: ["system-config"] as const,
  hoursPerIntensityPoint: () => ["system-config", "hours-per-intensity-point"] as const,
  solutionDeletionEnabled: () => ["system-config", "solution-deletion-enabled"] as const,
} as const;

export const systemLogKeys = {
  all: ["system-logs"] as const,
  list: (params?: {
    cursor?: string;
    direction?: string;
    level?: string;
    category?: string;
    status?: string;
    method?: string;
    path?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) => ["system-logs", "list", params ?? {}] as const,
  detail: (id: string) => ["system-logs", "detail", id] as const,
} as const;

export const solutionDashboardKeys = {
  all: ["dashboard-solutions"] as const,
  deletePreview: (ids: string[]) => ["dashboard-solutions", "delete-preview", ...ids] as const,
} as const;

export const signalKeys = {
  all: ["signals"] as const,
  list: (filter?: { type?: string; accountId?: string }) =>
    ["signals", "list", filter ?? {}] as const,
  stats: (window: number) => ["signals", "stats", window] as const,
  forSolution: (solutionId: string) => ["signals", "solution", solutionId] as const,
} as const;

/**
 * The Solution Detail drawer's own `["solution-detail", id]` query. Factored here
 * so mutations that change a Solution (e.g. confirming a Signal's Intensity) can
 * invalidate the open drawer without hard-coding the key at the call site.
 */
export const solutionDetailKeys = {
  all: ["solution-detail"] as const,
  detail: (id: string) => ["solution-detail", id] as const,
} as const;
